import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatRuntime } from '../../utils/filmWall.js';
import ControlBar from './ControlBar.jsx';
import YouTubePlayer from './YouTubePlayer.jsx';
import styles from './FilmCard.module.css';

// ─── Film card ────────────────────────────────────────────────────────────────
// The pop-up. A flat sheet in the film's colour grows out of the tile that was
// clicked (tilted mid-flight, like a print being slapped on a table), then the
// credits and the media stack deal in on top of it: two stills from the film
// fanned behind the player. Prev / next (or ← →) swaps films in place — the new
// stack slides over the old one while the sheet re-tints.
//
// Rendered through a portal so no transformed ancestor on the page can trap it.
// ─────────────────────────────────────────────────────────────────────────────

const OPEN_MS  = 760;
const CLOSE_MS = 520;
const REVEAL_MS = 300;   // content starts dealing in once the sheet is mostly grown
const SWAP_MS  = 700;

const formatLabel = (film) =>
  film.width < film.height ? `${film.aspect} Vertical` : `${film.aspect} Widescreen`;

// ─── mp4 player ───────────────────────────────────────────────────────────────
function Mp4Player({ film }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted]     = useState(false);
  const [ready, setReady]     = useState(false);
  // films.json `startAt` (seconds) skips a slow open; the loop comes back to it too
  const start = film.startAt ?? 0;
  const [time, setTime]       = useState(start);
  const [length, setLength]   = useState(film.duration ?? 0);

  // The click that opened the card counts as a gesture, so sound is allowed.
  // If the browser still refuses, fall back to muted rather than a dead player.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    v.play().catch(() => {
      v.muted = true;
      setMuted(true);
      v.play().catch(() => {});
    });
  }, []);

  const toggle = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {}); else v.pause();
  }, []);

  return (
    <>
      <video
        ref={videoRef}
        className={styles.media}
        src={start ? `${film.src}#t=${start}` : film.src}
        poster={film.poster}
        playsInline
        loop={!start}
        preload="auto"
        onClick={toggle}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onVolumeChange={(e) => setMuted(e.currentTarget.muted)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          const v = e.currentTarget;
          setLength(v.duration);
          // #t= is the fast path; this covers a browser that ignores the fragment
          if (start && v.currentTime < start - 0.25) v.currentTime = start;
        }}
        onEnded={(e) => {
          const v = e.currentTarget;
          v.currentTime = start;
          v.play().catch(() => {});
        }}
        onPlaying={() => setReady(true)}
      />
      {!ready && <span className={styles.buffering} aria-hidden="true" />}
      <ControlBar
        playing={playing}
        muted={muted}
        time={time}
        length={length}
        onToggle={toggle}
        onMute={() => { const v = videoRef.current; if (v) v.muted = !v.muted; }}
        onFullscreen={() => {
          const v = videoRef.current;
          if (v) (v.requestFullscreen ?? v.webkitEnterFullscreen)?.call(v);
        }}
        onSeekFraction={(f) => { const v = videoRef.current; if (v?.duration) v.currentTime = f * v.duration; }}
        onSeekBy={(d) => { const v = videoRef.current; if (v) v.currentTime = Math.max(0, v.currentTime + d); }}
      />
    </>
  );
}

// ─── Player ───────────────────────────────────────────────────────────────────
function Player({ film }) {
  return film.youtubeId ? <YouTubePlayer film={film} /> : <Mp4Player film={film} />;
}

// ─── Media stack ──────────────────────────────────────────────────────────────
function Stack({ film, outgoing = false }) {
  const style = { '--ar': film.width / film.height };
  const wide = film.width >= film.height;
  return (
    <div className={`${styles.stack} ${wide ? styles.stackWide : ''} ${outgoing ? styles.stackOut : ''}`}
      style={style} aria-hidden={outgoing || undefined}>
      <img className={`${styles.card} ${styles.still} ${styles.stillA}`} src={film.stills[0]} alt="" draggable="false" />
      <img className={`${styles.card} ${styles.still} ${styles.stillB}`} src={film.stills[1]} alt="" draggable="false" />
      <div className={`${styles.card} ${styles.player}`}>
        {outgoing
          ? <img className={styles.media} src={film.poster} alt="" draggable="false" />
          : <Player film={film} />}
      </div>
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────
export default function FilmCard({ film, index, total, getOrigin, onClose, onPrev, onNext, reduced }) {
  const frameRef = useRef(null);
  const sheetRef = useRef(null);
  const closeRef = useRef(null);
  const [phase, setPhase] = useState('enter');       // enter → open → leave
  const [outgoing, setOutgoing] = useState(null);
  const shownRef = useRef(film);
  const leavingRef = useRef(false);

  // Map the frame onto a tile's on-screen box: the transform that makes the
  // full-size sheet sit exactly over the tile, tilted.
  const fromTile = useCallback((rect, tilt) => {
    const f = frameRef.current.getBoundingClientRect();
    const dx = rect.left + rect.width / 2 - (f.left + f.width / 2);
    const dy = rect.top + rect.height / 2 - (f.top + f.height / 2);
    return `translate(${dx}px, ${dy}px) scale(${rect.width / f.width}, ${rect.height / f.height}) rotate(${tilt}deg)`;
  }, []);

  // ── Open ──
  useLayoutEffect(() => {
    const sheet = sheetRef.current;
    const origin = getOrigin?.(film.id);
    if (reduced || !sheet.animate) {
      setPhase('open');
    } else {
      const start = origin ? fromTile(origin, -4) : 'scale(0.86) rotate(-3deg)';
      sheet.animate(
        [
          { transform: start, opacity: origin ? 1 : 0 },
          { transform: 'translate(0, 0) scale(1.012) rotate(1.2deg)', opacity: 1, offset: 0.62 },
          { transform: 'none', opacity: 1 },
        ],
        { duration: OPEN_MS, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'both' },
      );
      const t = setTimeout(() => setPhase('open'), REVEAL_MS);
      closeRef.current?.focus({ preventScroll: true });
      return () => clearTimeout(t);
    }
    closeRef.current?.focus({ preventScroll: true });
    // Opening animation runs once, for the film the card was opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Close ──
  const close = useCallback(() => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    setPhase('leave');
    const sheet = sheetRef.current;
    if (reduced || !sheet?.animate) { onClose(); return; }
    const origin = getOrigin?.(shownRef.current.id);
    const end = origin ? fromTile(origin, -3) : 'scale(0.9) rotate(-2deg)';
    const anim = sheet.animate(
      [{ transform: 'none', opacity: 1 }, { transform: end, opacity: origin ? 1 : 0 }],
      { duration: CLOSE_MS, delay: 120, easing: 'cubic-bezier(0.65, 0, 0.35, 1)', fill: 'both' },
    );
    anim.onfinish = () => onClose();
  }, [reduced, getOrigin, fromTile, onClose]);

  // ── Swap film in place ──
  useEffect(() => {
    if (shownRef.current.id === film.id) return;
    setOutgoing(shownRef.current);
    shownRef.current = film;
    const t = setTimeout(() => setOutgoing(null), SWAP_MS);
    return () => clearTimeout(t);
  }, [film]);

  // ── Keys: Esc, ← →, and keep Tab inside the dialog ──
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowLeft')  onPrev?.();
      else if (e.key === 'ArrowRight') onNext?.();
      else if (e.key === 'Tab') {
        const nodes = frameRef.current?.querySelectorAll('button, [href], iframe, [tabindex]:not([tabindex="-1"])');
        if (!nodes?.length) return;
        const first = nodes[0], last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close, onPrev, onNext]);

  // Page behind must not scroll (the phone layout is a scrolling grid)
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const runtime = formatRuntime(film.duration);
  const titleId = 'film-card-title';

  return createPortal(
    <div
      className={styles.overlay}
      data-phase={phase}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      style={{ '--card': film.color, '--ink': film.ink }}
    >
      <div className={styles.backdrop} onClick={close} />

      <div className={styles.frame} ref={frameRef}>
        <div className={styles.sheet} ref={sheetRef} />

        <button ref={closeRef} type="button" className={styles.close} onClick={close} aria-label="Close">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <line x1="5" y1="5" x2="19" y2="19" /><line x1="19" y1="5" x2="5" y2="19" />
          </svg>
        </button>

        <div className={styles.body}>
          <div className={styles.info} key={`info-${film.id}`}>
            <p className={styles.count}>
              {String(index + 1).padStart(2, '0')} <span>/ {String(total).padStart(2, '0')}</span>
            </p>
            <h2 className={styles.title} id={titleId}>
              {film.title}<span className={styles.mark} aria-hidden="true">*</span>
            </h2>
            <p className={styles.sub}>{film.sub}</p>
            <dl className={styles.meta}>
              {film.year && <div><dt>Year</dt><dd>{film.year}</dd></div>}
              <div><dt>Format</dt><dd>{formatLabel(film)}</dd></div>
              {runtime && <div><dt>Runtime</dt><dd>{runtime}</dd></div>}
            </dl>
            <div className={styles.nav}>
              <button type="button" onClick={onPrev} aria-label="Previous film">← Prev</button>
              <button type="button" onClick={onNext} aria-label="Next film">Next →</button>
            </div>
          </div>

          <div className={styles.stage}>
            {outgoing && <Stack key={`out-${outgoing.id}`} film={outgoing} outgoing />}
            <Stack key={film.id} film={film} />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
